/**
 * E2EE crypto wrapper for the student PWA (Phase 1 — REAL crypto).
 *
 * Phase 0/1 implementation: the stubbed placeholders have been REPLACED with
 * real libsodium primitives. `await sodium.ready` MUST resolve before any call
 * here (the exported `ready` promise / `whenReady()` helper exposes it; callers
 * in tests await it in a `beforeAll`).
 *
 * CRYPTO MODEL (now real):
 *   - Identity: an X25519 keypair per student (`crypto_box_keypair`). The PUBLIC
 *     key is uploaded; the PRIVATE key is WRAPPED CLIENT-SIDE with a key derived
 *     from the student's password via Argon2id (`crypto_pwhash`) and only the
 *     opaque ciphertext + KDF params are sent to the server. The server NEVER
 *     sees a plaintext private key.
 *   - Sharing: a symmetric media key is shared by sealing it to a public key
 *     (`crypto_box_seal`) and stored server-side (opaque).
 *   - Media key: 32 random bytes (`generateMediaKey`) distributed base64url and
 *     fed to LiveKit's KeyProvider as an ArrayBuffer (`mediaKeyToArrayBuffer`).
 *   - QR flow: a device shows a SIGNED {key_id, key_material, exp, device_id,
 *     sig} payload as a QR. The student scans it, verifies signature + expiry,
 *     then re-seals key_material to their OWN public key and persists the
 *     ciphertext via POST /e2ee/shared-keys.
 *
 * PASSWORD-RESET CAVEAT (unchanged): the private key is wrapped by a
 * password-derived key. A normal password change requires re-wrapping; an admin
 * reset loses the old wrapping key and renders the wrapped private key
 * undecryptable until the identity is regenerated + keys re-shared.
 */
import sodium from "libsodium-wrappers-sumo";
import type { E2eeQrPayload, KdfParams, SetE2eeIdentityRequest } from "@diy-avatar/shared/e2ee";

/** Versioned prefix byte for the opaque wrapped-private-key blob. */
const WRAP_BLOB_VERSION = 0x01;

/** Resolves once libsodium's WASM is initialized. Await before any crypto. */
export const ready: Promise<void> = sodium.ready;

/** Convenience: await libsodium readiness (use in test `beforeAll`). */
export async function whenReady(): Promise<void> {
  await sodium.ready;
}

/** Base64url encode a UTF-8 string (browser-safe, no padding). */
export function b64url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Decode a base64url string back to UTF-8. */
export function unb64url(s: string): string {
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + pad);
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** base64url-encode raw bytes via libsodium (matches b64url variant: no pad). */
function bytesToB64(bytes: Uint8Array): string {
  return sodium.to_base64(bytes, sodium.base64_variants.URLSAFE_NO_PADDING);
}

/** base64url-decode to raw bytes via libsodium (URLSAFE_NO_PADDING). */
function b64ToBytes(b64: string): Uint8Array {
  return u8(sodium.from_base64(b64, sodium.base64_variants.URLSAFE_NO_PADDING));
}

/**
 * Normalize any byte array into a plain Uint8Array from THIS realm. Under jsdom
 * (vitest) the global `Uint8Array` can differ from the one libsodium validates
 * against, causing "unsupported input type" — copying into a fresh array fixes
 * the cross-realm instanceof check.
 */
function u8(bytes: Uint8Array | ArrayLike<number>): Uint8Array {
  const out = new Uint8Array((bytes as ArrayLike<number>).length);
  out.set(bytes as ArrayLike<number>);
  return out;
}

/** Encode a UTF-8 string to a realm-safe Uint8Array (avoids from_string). */
function strToBytes(s: string): Uint8Array {
  return u8(new TextEncoder().encode(s));
}

export interface GeneratedIdentity {
  /** X25519 public key (base64url). */
  publicKey: string;
  /** X25519 private key (base64url). Never sent unwrapped. */
  privateKey: string;
}

/**
 * Generate a fresh X25519 identity keypair (`crypto_box_keypair`).
 * Requires `await sodium.ready` first.
 */
export function generateIdentity(): GeneratedIdentity {
  const kp = sodium.crypto_box_keypair();
  return {
    publicKey: bytesToB64(u8(kp.publicKey)),
    privateKey: bytesToB64(u8(kp.privateKey)),
  };
}

/**
 * Default Argon2id KDF params with a fresh CSPRNG 16-byte salt.
 * Salt uses `crypto.getRandomValues` (NOT Math.random).
 */
export function defaultKdfParams(): KdfParams {
  const salt = new Uint8Array(sodium.crypto_pwhash_SALTBYTES);
  crypto.getRandomValues(salt);
  return {
    kdf: "argon2id",
    opslimit: sodium.crypto_pwhash_OPSLIMIT_INTERACTIVE,
    memlimit: sodium.crypto_pwhash_MEMLIMIT_INTERACTIVE,
    parallelism: 1,
    salt: bytesToB64(salt),
  };
}

/** Derive a 32-byte secretbox key from (password, salt) via Argon2id. */
function deriveWrappingKey(password: string, kdf: KdfParams, salt: Uint8Array): Uint8Array {
  return u8(
    sodium.crypto_pwhash(
      sodium.crypto_secretbox_KEYBYTES,
      password,
      u8(salt),
      kdf.opslimit,
      kdf.memlimit,
      sodium.crypto_pwhash_ALG_ARGON2ID13,
    ),
  );
}

/**
 * Wrap (encrypt) the private key with a password-derived Argon2id key, then
 * `crypto_secretbox_easy`. Output is an opaque versioned base64url blob bundling
 * [version || salt(16) || nonce(24) || ciphertext]. It contains NO PEM/"PRIVATE
 * KEY" markers (the backend shape guard rejects those).
 *
 * A 16-byte CSPRNG salt is generated here and bundled in the blob; `kdf.salt`
 * (if set) is overridden by the freshly-generated salt so the blob is
 * self-contained. Callers should persist the returned kdf via
 * `defaultKdfParams()` for params, but unwrap reads the salt FROM the blob.
 */
export function wrapPrivateKey(privateKey: string, password: string, kdf: KdfParams): string {
  const salt = new Uint8Array(sodium.crypto_pwhash_SALTBYTES);
  crypto.getRandomValues(salt);
  const key = deriveWrappingKey(password, kdf, salt);
  const nonce = u8(sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES));
  const ciphertext = u8(sodium.crypto_secretbox_easy(b64ToBytes(privateKey), nonce, key));

  const blob = new Uint8Array(1 + salt.length + nonce.length + ciphertext.length);
  let off = 0;
  blob[off] = WRAP_BLOB_VERSION;
  off += 1;
  blob.set(salt, off);
  off += salt.length;
  blob.set(nonce, off);
  off += nonce.length;
  blob.set(ciphertext, off);
  return bytesToB64(blob);
}

/**
 * Unwrap (decrypt) the private key. Reads salt + nonce from the opaque blob,
 * re-derives the Argon2id key from the password + `kdf` cost params, and opens
 * the secretbox. Returns the private key (base64url) or null on any failure
 * (wrong password, malformed blob).
 */
export function unwrapPrivateKey(wrapped: string, password: string, kdf: KdfParams): string | null {
  let blob: Uint8Array;
  try {
    blob = b64ToBytes(wrapped);
  } catch {
    return null;
  }
  const saltLen = sodium.crypto_pwhash_SALTBYTES;
  const nonceLen = sodium.crypto_secretbox_NONCEBYTES;
  if (blob.length < 1 + saltLen + nonceLen || blob[0] !== WRAP_BLOB_VERSION) return null;
  let off = 1;
  const salt = blob.slice(off, off + saltLen);
  off += saltLen;
  const nonce = blob.slice(off, off + nonceLen);
  off += nonceLen;
  const ciphertext = blob.slice(off);
  try {
    const key = deriveWrappingKey(password, kdf, salt);
    const plain = sodium.crypto_secretbox_open_easy(u8(ciphertext), u8(nonce), key);
    return bytesToB64(u8(plain));
  } catch {
    return null;
  }
}

/**
 * Seal a media key TO a public key (`crypto_box_seal`, anonymous sealed box).
 * `keyMaterial` is a base64url string (e.g. a 32-byte media key); it is encoded
 * as UTF-8 bytes and sealed. Output is opaque base64url ciphertext.
 */
export function encryptToPublicKey(keyMaterial: string, recipientPubB64: string): string {
  const pub = b64ToBytes(recipientPubB64);
  const sealed = sodium.crypto_box_seal(strToBytes(keyMaterial), pub);
  return bytesToB64(u8(sealed));
}

/**
 * Open a sealed media key with the (unwrapped) keypair (`crypto_box_seal_open`).
 * Requires BOTH the public and private key (sealed boxes need the pubkey to
 * recompute the ephemeral shared secret). Returns the plaintext or null.
 */
export function decryptWithPrivateKey(
  cipherB64: string,
  pubB64: string,
  privB64: string,
): string | null {
  try {
    const sealed = b64ToBytes(cipherB64);
    const pub = b64ToBytes(pubB64);
    const priv = b64ToBytes(privB64);
    const plain = sodium.crypto_box_seal_open(sealed, pub, priv);
    return new TextDecoder().decode(u8(plain));
  } catch {
    return null;
  }
}

/** Generate a fresh 32-byte symmetric media key (base64url). */
export function generateMediaKey(): string {
  return bytesToB64(u8(sodium.randombytes_buf(32)));
}

/**
 * Derive a deterministic key_id from raw key material: base64url(sha256(material)).
 * Used by the MANUAL-PASTE provisioning path (Part B) when a bare base64url key
 * is pasted without a QR envelope, so the stored row still carries a stable id.
 * The material is hashed as UTF-8 bytes (same encoding `encryptToPublicKey`
 * seals), so two identical pasted strings always yield the same key_id.
 */
export function keyIdForMaterial(keyMaterial: string): string {
  const digest = sodium.crypto_hash_sha256(strToBytes(keyMaterial));
  return bytesToB64(u8(digest));
}

/**
 * Derive the X25519 PUBLIC key (base64url) from a private key (base64url) via
 * `crypto_scalarmult_base`. Used by the recovery flow to reconstruct the full
 * identity from just the recovered private key. Returns null on bad input.
 */
export function publicKeyFromPrivate(privB64: string): string | null {
  try {
    const priv = b64ToBytes(privB64);
    const pub = sodium.crypto_scalarmult_base(u8(priv));
    return bytesToB64(u8(pub));
  } catch {
    return null;
  }
}

/**
 * Generate a human-friendly RECOVERY CODE: 16 CSPRNG bytes rendered as upper-
 * case hex, grouped in 4-char blocks with dashes (e.g. "1A2B-3C4D-...") for
 * readable transcription. Use {@link normalizeRecoveryCode} before wrapping /
 * unwrapping so formatting (dashes, case, spaces) never affects the derived key.
 */
export function generateRecoveryCode(): string {
  const bytes = u8(sodium.randombytes_buf(16));
  let hex = "";
  for (const b of bytes) hex += b.toString(16).padStart(2, "0").toUpperCase();
  return (hex.match(/.{1,4}/g) ?? [hex]).join("-");
}

/** Strip formatting from a recovery code → canonical uppercase hex string. */
export function normalizeRecoveryCode(code: string): string {
  return code.replace(/[^0-9a-fA-F]/g, "").toUpperCase();
}

/**
 * Decode a base64url media key to an ArrayBuffer for LiveKit's KeyProvider
 * `setKey(arrayBuffer)`. Pair with `E2EE_KEY_PROVIDER_OPTS` for parity.
 */
export function mediaKeyToArrayBuffer(b64: string): ArrayBuffer {
  const bytes = b64ToBytes(b64);
  // Return a tight copy as a standalone ArrayBuffer.
  const out = new Uint8Array(bytes.length);
  out.set(bytes);
  return out.buffer;
}

/**
 * First-time identity setup: generate a keypair, wrap the private key with the
 * password, and return the request body for PUT /me/e2ee/identity. Pure — the
 * caller performs the network write so this stays testable.
 */
export function buildIdentitySetup(password: string): {
  request: SetE2eeIdentityRequest;
  /** The freshly-generated identity (keep private key in memory only). */
  identity: GeneratedIdentity;
  kdf: KdfParams;
} {
  const identity = generateIdentity();
  const kdf = defaultKdfParams();
  const wrapped = wrapPrivateKey(identity.privateKey, password, kdf);
  return {
    identity,
    kdf,
    request: {
      public_key: identity.publicKey,
      privkey_wrapped: wrapped,
      kdf_params: JSON.stringify(kdf),
    },
  };
}

/**
 * Canonicalize the SIGNED fields of a QR payload into the exact UTF-8 JSON the
 * device signed (and the scanner verifies). `sig` is EXCLUDED; key order is
 * fixed. See the contract note in shared/src/e2ee.ts.
 */
export function canonicalizeQrPayload(p: E2eeQrPayload): Uint8Array {
  const canonical = {
    v: p.v,
    kind: p.kind,
    scope: p.scope,
    scope_id: p.scope_id,
    key_id: p.key_id,
    key_material: p.key_material,
    exp: p.exp,
    device_id: p.device_id,
  };
  return strToBytes(JSON.stringify(canonical));
}

export interface VerifyQrResult {
  ok: boolean;
  /** Reason when !ok: "expired" | "no_sig" | "no_key" | "bad_sig". */
  reason?: "expired" | "no_sig" | "no_key" | "bad_sig";
}

/**
 * Verify a QR payload's expiry and (optionally) its Ed25519 signature.
 *
 * - Always checks `exp` (if present) against `now` and rejects when elapsed.
 * - If `deviceSignPubB64` is provided, verifies `sig` via
 *   `crypto_sign_verify_detached` over the canonical payload.
 * - In this phase the device sign-key distribution is a HOOK: when no key is
 *   provided, signature verification is SKIPPED (only expiry is enforced) and
 *   the result is ok as long as the payload has not expired.
 */
export function verifyQrPayload(
  payload: E2eeQrPayload,
  opts: { deviceSignPubB64?: string; now?: number } = {},
): VerifyQrResult {
  const now = opts.now ?? Date.now();
  if (typeof payload.exp === "number" && now >= payload.exp) {
    return { ok: false, reason: "expired" };
  }
  // No device key in this phase -> verify expiry only (hook exposed).
  if (!opts.deviceSignPubB64) {
    return { ok: true };
  }
  if (!payload.sig) return { ok: false, reason: "no_sig" };
  try {
    const valid = sodium.crypto_sign_verify_detached(
      b64ToBytes(payload.sig),
      u8(canonicalizeQrPayload(payload)),
      b64ToBytes(opts.deviceSignPubB64),
    );
    return valid ? { ok: true } : { ok: false, reason: "bad_sig" };
  } catch {
    return { ok: false, reason: "bad_sig" };
  }
}

/** Type guard: validate a decoded QR string is a well-formed E2EE payload. */
export function parseQrPayload(text: string): E2eeQrPayload | null {
  let obj: unknown;
  try {
    obj = JSON.parse(text);
  } catch {
    return null;
  }
  if (!obj || typeof obj !== "object") return null;
  const o = obj as Record<string, unknown>;
  if (o.v !== 1 || o.kind !== "e2ee-key") return null;
  if (o.scope !== "room" && o.scope !== "device") return null;
  if (typeof o.scope_id !== "number" || !Number.isFinite(o.scope_id)) return null;
  if (typeof o.key_id !== "string" || o.key_id.length === 0) return null;
  if (typeof o.key_material !== "string") return null;
  const out: E2eeQrPayload = {
    v: 1,
    kind: "e2ee-key",
    scope: o.scope,
    scope_id: o.scope_id,
    key_id: o.key_id,
    key_material: o.key_material,
  };
  if (typeof o.exp === "number" && Number.isFinite(o.exp)) out.exp = o.exp;
  if (typeof o.device_id === "string") out.device_id = o.device_id;
  if (typeof o.sig === "string") out.sig = o.sig;
  return out;
}

/**
 * Process a scanned device QR: verify it (expiry + optional signature), then
 * re-seal the real key material to the student's OWN public key, producing the
 * StoreSharedKeyRequest the caller POSTs. Returns null when the payload is
 * invalid OR verification fails.
 *
 * `ownPublicKey` is the student's X25519 public key (base64url).
 * `verify.deviceSignPubB64` (optional) enables Ed25519 signature checking; when
 * omitted only expiry is enforced (phase-appropriate hook).
 */
export function buildSharedKeyFromQr(
  text: string,
  ownPublicKey: string,
  verify: { deviceSignPubB64?: string; now?: number } = {},
): {
  payload: E2eeQrPayload;
  request: {
    scope: E2eeQrPayload["scope"];
    scope_id: number;
    key_ciphertext: string;
    key_id: string;
  };
} | null {
  const payload = parseQrPayload(text);
  if (!payload) return null;
  const v = verifyQrPayload(payload, verify);
  if (!v.ok) return null;
  const ciphertext = encryptToPublicKey(payload.key_material, ownPublicKey);
  return {
    payload,
    request: {
      scope: payload.scope,
      scope_id: payload.scope_id,
      key_ciphertext: ciphertext,
      // Phase 6: carry the scanned key_id so the backend can revoke precisely on
      // the next rotation (a re-scanned student keeps their fresh row).
      key_id: payload.key_id,
    },
  };
}
